import { ReactNode, ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  glow?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  glow = false,
  loading = false,
  fullWidth = false,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      disabled={loading || disabled}
      className={clsx(
        // Base styles
        'font-semibold rounded-lg transition-all duration-300',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-bg',
        'relative overflow-hidden',

        // Variant styles
        {
          // Primary: Orange gradient with glow
          'bg-gradient-to-r from-orange-500 to-orange-600 text-white':
            variant === 'primary',
          'hover:from-orange-600 hover:to-orange-700': variant === 'primary',
          'focus:ring-orange-500': variant === 'primary' || variant === 'secondary',
          'shadow-lg shadow-orange-500/30': variant === 'primary' && glow,
          'hover:shadow-orange-500/50': variant === 'primary' && glow,

          // Secondary: Outlined orange
          'border-2 border-orange-500 text-orange-500 bg-transparent':
            variant === 'secondary',
          'hover:bg-orange-500 hover:text-white': variant === 'secondary',

          // Ghost: Transparent with hover
          'text-slate-300 hover:text-white hover:bg-slate-700/50':
            variant === 'ghost',
          'focus:ring-slate-500': variant === 'ghost',

          // Danger: Red gradient
          'bg-gradient-to-r from-red-500 to-red-600 text-white':
            variant === 'danger',
          'hover:from-red-600 hover:to-red-700': variant === 'danger',
          'focus:ring-red-500': variant === 'danger',

          // Success: Green gradient
          'bg-gradient-to-r from-green-500 to-green-600 text-white':
            variant === 'success',
          'hover:from-green-600 hover:to-green-700': variant === 'success',
          'focus:ring-green-500': variant === 'success',

          // Sizes
          'px-3 py-1.5 text-sm': size === 'sm',
          'px-4 py-2 text-base': size === 'md',
          'px-6 py-3 text-lg': size === 'lg',

          // Full width
          'w-full': fullWidth,
        },
        className
      )}
      {...props}
    >
      {/* Shimmer effect on hover */}
      <span
        className={clsx(
          'absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500',
          'bg-gradient-to-r from-transparent via-white/10 to-transparent',
          'animate-shimmer'
        )}
        style={{ backgroundSize: '200% 100%' }}
      />

      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-2">
        {loading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </span>
    </button>
  );
}
